import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import instance from "../utils/axiosInstance";
import { ethers } from "ethers";
import {
  Box, Grid, Typography, Button, Divider, Tabs, Tab,
  FormControl, InputLabel, MenuItem, Select,
  IconButton, Chip, CircularProgress, Dialog, DialogTitle,
  DialogActions, TextField, Container, Snackbar, DialogContent,
  DialogContentText, Alert, Tooltip, Avatar
} from "@mui/material";
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import Autocomplete from "@mui/material/Autocomplete";
import SellModal from "../components/SellModal_test";
import { getMetaFromTokenURI, getIpfsUrl } from "../utils/meta_in_token";
import NFTCard from "../components/NFTCard";

const NFTDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [likeCount, setLikeCount] = useState(0);
  const [nft, setNft] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [liked, setLiked] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [tags, setTags] = useState([]);
  const [relatedNFTs, setRelatedNFTs] = useState([]);
  const [snackbar, setSnackbar] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [transferHistory, setTransferHistory] = useState([]);
  
  const [gatewayIndex, setGatewayIndex] = useState(0);
  const API_BASE_URL = process.env.REACT_APP_BACK_URL;

  const gateways = [
    "https://cloudflare-ipfs.com/ipfs/",
    "https://gateway.pinata.cloud/ipfs/",
    "https://dweb.link/ipfs/",
    "https://teal-traditional-pig-34.mypinata.cloud/ipfs/",
    "https://ipfs.io/ipfs/",
  ];
  
  
  
  const isOwner = user && nft?.userId?._id === user._id;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        // 1. NFT 기본 정보 불러오기
        const nftRes = await instance.get(`/detail/${id}`);
        const raw = nftRes.data.nft;
        const meta = await getMetaFromTokenURI(raw.tokenURI);
  
        const loadedNFT = {
          ...raw,
          name: meta?.name || raw.name,
          description: meta?.description || raw.description,
          displayImage: getIpfsUrl(meta?.image),
        };
        setNft(loadedNFT);
        setLikeCount(raw.liked || 0);
        setTags(raw.tags || []);
  
        // 2. 좋아요 여부 확인
        if (user && raw._id) {
          const likedRes = await instance.get(`/user/is_liked/${raw._id}`, { withCredentials: true });
          setLiked(likedRes.data.liked);
        }
  
        // 3. NFT 불러온 후 -> 관련 NFT 불러오기
        if (loadedNFT.creator) {
          const relatedRes = await instance.get(`/nftmanage/related?creator=${encodeURIComponent(loadedNFT.creator)}&exclude=${id}`);
          const relatedData = await Promise.all(
            (relatedRes.data || []).map(async (item) => {
              const meta = await getMetaFromTokenURI(item.tokenURI);
              return {
                ...item,
                name: meta?.name,
                displayImage: getIpfsUrl(meta?.image || "")
              };
            })
          );
          setRelatedNFTs(relatedData.slice(0, 3));
        }
  
        // 4. 댓글 불러오기
        const commentsRes = await instance.get(`/nftmanage/comment/${id}`);
        setComments(commentsRes.data.comments);
  
        // 5. 지갑 목록 불러오기
        if (user) {
          const walletRes = await instance.get("/wallet/me", { withCredentials: true });
          setWallets(walletRes.data.wallets || []);
          if (walletRes.data.wallets.length > 0) {
            setSelectedWallet(walletRes.data.wallets[0].address);
          }
        }
        

        const transferRes = await instance.get(`/nftmanage/transfer/${raw.nftAddress}/${raw.tokenID}`);
        setTransferHistory((transferRes.data.transfers || []).slice(0, 10));
  
      } catch (err) {
        console.error("NFT 조회 중 오류:", err);
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
  
    fetchAll();
  }, [id, user, navigate]);

  const handleAddComment = async () => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    if (!newComment.trim()) return alert("댓글을 입력하세요.");
  
    try {
      await instance.post(`/nftmanage/comment/${id}`, { content: newComment }, { withCredentials: true });
      setNewComment(""); // 입력창 비우기
  
      // 댓글 다시 불러오기
      const res = await instance.get(`/nftmanage/comment/${id}`);
      setComments(res.data.comments);
    } catch (err) {
      console.error("댓글 작성 실패:", err);
      alert(err.response?.data?.message || "댓글 작성 실패");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("댓글을 삭제하시겠습니까?")) return;
  
    try {
      await instance.delete(`/nftmanage/comment/${id}/${commentId}`, { withCredentials: true });
      // 삭제 성공 후, 댓글 목록 새로고침
      const res = await instance.get(`/nftmanage/comment/${id}`);
      setComments(res.data.comments);
    } catch (err) {
      console.error("댓글 삭제 실패:", err);
      alert(err.response?.data?.message || "댓글 삭제 실패");
    }
  };

  const handleBuy = async () => {
    if (!nft?.isForSale) return alert("판매 중이 아닙니다.");
    if (!selectedWallet || !password) return alert("지갑과 비밀번호를 입력해주세요.");
    if (!window.confirm("정말 구매하시겠습니까?")) return;

    setIsPurchasing(true);
    try {
      await instance.post("/order/purchase", {
        orderId: nft.isForSale._id,
        walletAddress: selectedWallet,
        password,
      }, { withCredentials: true });

      alert("구매 완료");
      navigate("/profile");
    } catch (err) {
      alert(err.response?.data?.message || "구매 실패");
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleLike = async () => {
    if (!user) return setShowLoginPrompt(true);
    try {
      const res = await instance.post(`/nftmanage/like/${nft._id}`, {}, { withCredentials: true });
      setLiked(res.data.liked);
      setLikeCount(prev => res.data.liked ? prev + 1 : Math.max(prev - 1, 0));
    } catch (err) {
      console.error("좋아요 실패", err);
    }
  };

  const handleTabChange = (_, val) => setTabIndex(val);
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar(true);
  };

  if (loading || !nft) {
    return <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>;
  }
  

  
  const ipfsHash = nft.displayImage
    ?.replace("ipfs://", "")
    .replace("https://ipfs.io/ipfs/", "")
    .replace("https://cloudflare-ipfs.com/ipfs/", "")
    .replace("https://gateway.pinata.cloud/ipfs/", "")
    .replace("https://dweb.link/ipfs/", "");
  
  const currentImageUrl = `${gateways[gatewayIndex]}${ipfsHash}`;

  const rows = [
    { label: "크리에이터", value: nft.creator || "-" },
    { label: "소유자", value: nft.ownerAddress },
    { label: "토큰 ID", value: nft.tokenID },
    { label: "계약", value: nft.nftAddress },
    { label: "표준", value: "ERC-721" },
  ];

  return (
<Container sx={(theme) => ({ mt: 6, bgcolor: theme.palette.background.default, py: 4 })}>
  <Grid container spacing={6}>
    {/* NFT 이미지 */}
    <Grid item xs={12} md={6}>
      <Box display="flex" justifyContent="center">
        <Box
          component="img"
          src={currentImageUrl}
          alt="NFT"
          sx={{ width: "100%", maxWidth: 400, borderRadius: 3, bgcolor: "background.paper" }}
          crossOrigin="anonymous"
          onError={() => {
            if (gatewayIndex < gateways.length - 1) {
              setGatewayIndex((prev) => prev + 1);
            }
          }}
        />
      </Box>
    </Grid>


    {/* NFT 정보 */}
    <Grid item xs={12} md={6}>
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="h4" fontWeight="bold" color="text.primary">
        {nft.name}
      </Typography>

      {/* 🔗 OpenSea 링크 아이콘 버튼 */}
      <Tooltip title="OpenSea에서 보기">
        <IconButton
          size="small"
          href={`https://testnets.opensea.io/assets/sepolia/${nft.nftAddress}/${nft.tokenID}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/opensea-icon.png"  // 아이콘 경로 (PNG 또는 SVG)
            alt="OpenSea"
            style={{ width: 20, height: 20 }}
          />
        </IconButton>
      </Tooltip>
    </Box>

      <Typography color="text.secondary" sx={{ mt: 1 }}>{nft.creator} 작가</Typography>
      <Typography sx={{ mt: 1 }}>{nft.isForSale ? `${ethers.formatEther(nft.isForSale.price)} ETH` : "판매 중 아님"}</Typography>

      {nft.isForSale && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {nft.isForSale.isLazy ? "Lazy Mint 상태 (거래 시 민팅됨)" : "온체인 민팅 완료됨"}
        </Typography>
      )}
      <Box mt={2} display="flex" gap={2} alignItems="center">
        <IconButton onClick={handleLike} color={liked ? "error" : "default"}>
          {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </IconButton>

        {isOwner ? (
          nft.isForSale ? (
            <Button variant="outlined" color="error" onClick={() =>
              instance.delete(`/order/cancel/${nft.isForSale._id}`, { withCredentials: true })
                .then(() => window.location.reload())
            }>
              판매 취소
            </Button>
          ) : (
            <Button variant="contained" onClick={() => setShowModal(true)}>판매 등록</Button>
          )
        ) : (
          nft.isForSale && (
            <Button variant="contained" onClick={() => setShowBuyModal(true)}>
              구매하기
            </Button>
          )
        )}
      </Box>
      

      {/* NFT 소유자 정보 표시 */}
      {nft.userId && (
      <Box
        component={Link}
        to={`/user/${nft.userId._id}`}
        sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, textDecoration: "none" }}
      >
        <Avatar
          src={
            nft.userId?.profileImage
              ? `${API_BASE_URL}${nft.userId.profileImage}`
              : undefined
          }
          sx={{ width: 40, height: 40 }}
        />

        <Typography color="text.secondary">
          소유자: {nft.userId.name || "알 수 없음"}
        </Typography>
      </Box>
    )}

      <Typography mt={3} color="text.primary">{nft.description}</Typography>

      {/* 태그 */}
      {isOwner && (
        <Box mt={4}>
          <Typography variant="h6" fontWeight="bold">태그</Typography>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={tags}
            onChange={async (_, newTags) => {
              setTags(newTags);
              try {
                await instance.put(`/detail/${nft._id}/tags`, { tags: newTags }, { withCredentials: true });
              } catch (e) {
                console.warn("태그 저장 실패:", e);
              }
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="엔터로 추가" variant="outlined" fullWidth />
            )}
          />
        </Box>
      )}

      <Divider sx={{ my: 4 }} />
      <Tabs value={tabIndex} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
        
        <Tab label="댓글" />
        <Tab label="세부" />
        <Tab label="다른 작품" />
        <Tab label="거래내역" />
      </Tabs>

      {tabIndex === 0 && (
        <Box mt={4}>
          <Typography variant="h6" fontWeight="bold">댓글</Typography>

          {/* 댓글 작성 부분 */}
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <TextField
              label="댓글 작성"
              variant="outlined"
              fullWidth
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={handleAddComment}
              sx={{ minWidth: 100, height: "56px" }} // 높이는 TextField와 맞춤
            >
              작성
            </Button>
          </Box>


          {/* 기존 댓글 목록 */}
          {comments.length === 0 ? (
            <Typography color="text.secondary" mt={4}>아직 댓글이 없습니다.</Typography>
          ) : (
            comments.map((comment) => (
              <Box key={comment._id} mt={4} p={2} bgcolor="background.paper" borderRadius={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {comment.user?.name || "알 수 없음"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(comment.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
            
                  {/* 🔥 본인 댓글이면 삭제 버튼 표시 */}
                  {comment.user?._id === user._id && (
                    <Button size="small" color="error" onClick={() => handleDeleteComment(comment._id)}>
                      삭제
                    </Button>
                  )}
                </Box>
                <Typography variant="body1" mt={1}>
                  {comment.content}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}

      {tabIndex === 1 && (
        <Box mt={2}>
          {rows.map((row, idx) => (
            <Box key={idx} py={1} display="flex" alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                {row.label}
              </Typography>
              <Tooltip title={row.value} arrow>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer",
                    maxWidth: "70%",
                  }}
                  onClick={() => handleCopy(row.value)}
                >
                  {row.value}
                </Typography>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {tabIndex === 2 && (
        <Box mt={2}>
          <Typography fontWeight="bold" gutterBottom>같은 작가의 다른 작품</Typography>
          <Grid container spacing={2}>
            {relatedNFTs.map((item, idx) => (
              <Grid item xs={4} key={idx}>
                <NFTCard item={item} index={idx} onClick={() => navigate(`/nft/${item._id}`)} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {tabIndex === 3 && (
        <Box mt={4}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            최근 거래내역
          </Typography>

          {transferHistory.length === 0 ? (
            <Typography color="text.secondary" mt={2}>거래 기록 없음</Typography>
          ) : (
            transferHistory.map((transfer, index) => {
              const date = transfer.timestamp 
                ? new Date(transfer.timestamp * 1000).toLocaleString()
                : "날짜 정보 없음";

              return (
                <Box key={index} mt={2} p={2} bgcolor="background.paper" borderRadius={2}>
                  {transfer.isMint ? (
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      민팅됨 ({date})
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" fontWeight="bold" color="error.main">
                        거래됨 {transfer.value ? `- ${transfer.value} ETH` : ""}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {date}
                      </Typography>
                    </>
                  )}
                  <Typography variant="body2" color="primary" component="a" href={`https://sepolia.etherscan.io/tx/${transfer.transactionHash}`} target="_blank" rel="noopener noreferrer">
                    트랜잭션 보기
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      )}



      
    </Grid>
  </Grid>

  {/* 다이얼로그 및 스낵바 */}
  <Dialog open={showLoginPrompt} onClose={() => setShowLoginPrompt(false)}>
    <DialogTitle>로그인이 필요합니다</DialogTitle>
    <DialogActions>
      <Button onClick={() => setShowLoginPrompt(false)}>취소</Button>
      <Button onClick={() => navigate("/login")}>로그인 하러가기</Button>
    </DialogActions>
  </Dialog>

  <Snackbar
    open={snackbar}
    autoHideDuration={2000}
    onClose={() => setSnackbar(false)}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
  >
    <Alert severity="success" sx={{ width: "100%" }}>
      복사됨!
    </Alert>
  </Snackbar>

  {/* 구매 다이얼로그 */}
  <Dialog open={showBuyModal} onClose={() => !isPurchasing && setShowBuyModal(false)}>
    <DialogTitle>구매하기</DialogTitle>
    <DialogContent>
      <DialogContentText>지갑과 비밀번호를 입력하세요.</DialogContentText>
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>지갑 주소</InputLabel>
        <Select
          value={selectedWallet}
          label="지갑 주소"
          onChange={(e) => setSelectedWallet(e.target.value)}
          disabled={isPurchasing}
        >
          {wallets.map((wallet) => (
            <MenuItem key={wallet._id} value={wallet.address}>{wallet.address}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="지갑 비밀번호"
        type="password"
        fullWidth
        sx={{ mt: 2 }}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={isPurchasing}
      />
      {isPurchasing && (
        <Box mt={2} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={20} />
          <Typography variant="body2">구매 진행 중...</Typography>
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setShowBuyModal(false)} disabled={isPurchasing}>취소</Button>
      <Button onClick={handleBuy} disabled={isPurchasing}>
        {isPurchasing ? "구매 중..." : "구매하기"}
      </Button>
    </DialogActions>
  </Dialog>

  {showModal && (
    <SellModal
      nftId={nft._id}
      tokenId={nft.tokenID}
      tokenURI={nft.tokenURI}
      nftAddress={nft.nftAddress}
      approved={nft.approved}
      onClose={() => setShowModal(false)}
      onSold={() => window.location.reload()}
    />
  )}
</Container>
  );
};

export default NFTDetail;



